/**
 * Durable queue with localStorage persistence and size/TTL caps.
 */
import type { StorageAdapter } from "./storage";
import { now } from "./utils";

export type QueuedEvent = {
  eventId: string;
  type: string;
  occurredAt: number;
  props?: Record<string, unknown>;
  enqueuedAt: number;
  attempts: number;
};

export type QueueConfig = {
  key: string;
  maxEvents: number;
  maxBytes: number;
  ttlMs: number;
};

export type Queue = {
  enqueue: (event: QueuedEvent) => void;
  drain: () => QueuedEvent[];
  size: () => number;
  clear: () => void;
};

export function createQueue(
  storage: StorageAdapter,
  config: QueueConfig,
): Queue {
  let items: QueuedEvent[] = [];

  const load = () => {
    const raw = storage.get(config.key);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as QueuedEvent[];
      items = parsed.filter(
        (event) => now() - event.enqueuedAt <= config.ttlMs,
      );
    } catch {
      items = [];
    }
  };

  const persist = () => {
    try {
      const payload = JSON.stringify(items);
      // Trim oldest events when exceeding size limits.
      if (payload.length > config.maxBytes) {
        while (
          items.length > 0 &&
          JSON.stringify(items).length > config.maxBytes
        ) {
          items.shift();
        }
      }
      storage.set(config.key, JSON.stringify(items));
    } catch {
      // ignore
    }
  };

  load();

  const enqueue = (event: QueuedEvent) => {
    items.push(event);
    if (items.length > config.maxEvents) {
      items.splice(0, items.length - config.maxEvents);
    }
    persist();
  };

  const drain = () => {
    const batch = items.slice();
    items = [];
    storage.remove(config.key);
    return batch;
  };

  const size = () => items.length;

  const clear = () => {
    items = [];
    storage.remove(config.key);
  };

  return { enqueue, drain, size, clear };
}
